import { useEffect, useState } from 'react';
import { History, Upload } from 'lucide-react';
import { api, ApiClientError } from '@/api/client';
import type { KbVersion } from '@/types';

export function Versions() {
  const [versions, setVersions] = useState<KbVersion[] | null>(null);
  const [error, setError]       = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [notes, setNotes]       = useState('');

  async function load() {
    setError(null);
    try { setVersions(await api.listVersions()); }
    catch (e) { setError(e instanceof ApiClientError ? `${e.code}: ${e.message}` : (e as Error).message); }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function publish() {
    if (!window.confirm('Publish a new KB snapshot from all currently-published workflows?')) return;
    setPublishing(true); setError(null);
    try {
      const result = await api.publishKb(notes.trim() || undefined);
      setNotes('');
      await load();
      alert(`Published v${result.version} with ${result.workflow_count} workflow(s).`);
    } catch (e) {
      setError(e instanceof ApiClientError ? `${e.code}: ${e.message}` : (e as Error).message);
    } finally { setPublishing(false); }
  }

  return (
    <div className="max-w-5xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-8">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Versions</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Published KB snapshots</p>
        </div>
      </div>

      {/* Publish card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 sm:p-6 mb-6">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Publish a new version</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Snapshots all workflows currently in{' '}
          <span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded">published</span> status into an
          immutable JSON file the agent can serve from.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <input
            type="text"
            className="input flex-1"
            placeholder="Optional notes (e.g. 'Added prescription workflow')"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <button onClick={publish} disabled={publishing} className="btn-primary sm:flex-shrink-0">
            <Upload size={16} className="mr-2" />
            {publishing ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400 mb-4">{error}</div>
      )}

      {/* Versions table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {versions === null && <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading…</div>}

        {versions && versions.length === 0 && (
          <div className="p-12 text-center">
            <History size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">No versions yet</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Publish your first version when you're ready for the agent to use your workflows.
            </p>
          </div>
        )}

        {versions && versions.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Version</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Published</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Workflows</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium hidden sm:table-cell">Notes</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium hidden lg:table-cell">JSON</th>
                </tr>
              </thead>
              <tbody>
                {versions.map((v) => (
                  <tr key={v.id} className="border-b border-gray-100 dark:border-gray-700">
                    <td className="py-3 px-4 font-mono font-semibold text-gray-900 dark:text-white">v{v.version_number}</td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400 text-xs whitespace-nowrap">
                      {new Date(v.published_at).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{v.workflow_count}</td>
                    <td className="py-3 px-4 text-gray-700 dark:text-gray-300 hidden sm:table-cell">
                      {v.notes || <span className="text-gray-400 dark:text-gray-500">—</span>}
                    </td>
                    <td className="py-3 px-4 text-xs hidden lg:table-cell">
                      <span className="font-mono break-all text-gray-500 dark:text-gray-400" title={v.json_url}>{v.json_url}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
